import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Library } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { FileUploader } from '@/components/forms/FileUploader';
import { useT } from '@/lib/i18n/useT';
import { LibraryPickerDialog } from './LibraryPickerDialog';
import { prettyName, type LibraryItem } from './libraryItems';

/**
 * Game-logo input. Combines the free-form uploader with the curated library
 * served from `GET /api/v1/upload/wheel-logos`. Ops add logos by dropping
 * files into `ox-platform/uploads/wheel-logos/` — no migration, no rebuild.
 *
 * CH-17: the library used to unfold in line, four columns inside half a
 * dialog column, so every logo rendered at ~20 px. It now opens in
 * `LibraryPickerDialog`, above the form, with legible tiles and search.
 *
 * `value` is the canonical image url whichever surface produced it;
 * downstream code resolves it via `resolveImageUrl`.
 */
export function WheelLogoPicker({
  value,
  onChange,
  onUploadingChange,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const libraryQ = useQuery<LibraryItem[]>({
    queryKey: queryKeys.library.wheelLogos,
    queryFn: () => api.get<LibraryItem[]>('/upload/wheel-logos'),
    staleTime: 5 * 60_000,
  });
  const items = libraryQ.data ?? [];
  const picked = value ? items.find((i) => i.url === value) : undefined;

  return (
    <div className="space-y-2">
      <FileUploader
        value={value}
        onChange={onChange}
        onUploadingChange={onUploadingChange}
        preset="logo"
      />

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-accent-ink)]"
      >
        <span className="inline-flex items-center gap-1.5">
          <Library className="h-3.5 w-3.5" />
          {t('stock.dialog.field.wheelLogoLibrary')}
        </span>
        {items.length > 0 && (
          <span className="rounded-full bg-[var(--color-accent-10)] px-1.5 text-[10px] tabular-nums text-[var(--color-accent-ink)]">
            {items.length}
          </span>
        )}
      </button>

      {picked && (
        <p className="flex items-center justify-between gap-2 text-[11px] text-[var(--color-xk-text-muted)]">
          <span className="truncate">{t('stock.library.pickedHint', { name: prettyName(picked.name) })}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="shrink-0 font-semibold text-[var(--color-accent-ink)] hover:underline"
          >
            {t('common.remove')}
          </button>
        </p>
      )}

      <LibraryPickerDialog
        open={open}
        onOpenChange={setOpen}
        title={t('stock.library.title')}
        items={items}
        value={value}
        onPick={(url) => onChange(url)}
        isLoading={libraryQ.isLoading}
        emptyLabel={t('stock.dialog.field.wheelLogoLibraryEmpty')}
      />
    </div>
  );
}
